import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest
} from "./api.request";

// Import jsPDF for PDF generation
import { jsPDF } from 'jspdf';

// Helper function to check if browser supports file downloads
const isDownloadSupported = () => {
  const link = document.createElement('a');
  return link.download !== undefined;
};

// Helper function to safely extract array data from API responses
export const extractArrayFromResponse = (response, possibleArrayKeys = ['records', 'data', 'payments', 'bills', 'collections', 'summaries']) => {
  if (!response?.data) {
    return [];
  }

  // If response.data is already an array
  if (Array.isArray(response.data)) {
    return response.data;
  }

  // Check for common array keys in the response
  for (const key of possibleArrayKeys) {
    if (response.data[key] && Array.isArray(response.data[key])) {
      return response.data[key];
    }
  }

  // If no array found, return empty array
  return [];
};

// Helper function to safely get total count from response
export const extractTotalFromResponse = (response, dataArray) => {
  if (!response?.data) {
    return 0;
  }

  return response.data.totalRecords || 
         response.data.total || 
         response.data.count || 
         dataArray.length || 
         0;
};

// Excel export utility (using xlsx library if available, otherwise fallback to CSV)
const exportToExcel = (data, filename, sheetName = 'Report') => {
  try {
    // Try to use xlsx library if available
    if (window.XLSX) {
      const wb = window.XLSX.utils.book_new();
      const ws = window.XLSX.utils.json_to_sheet(data);
      
      // Set column widths for better visibility and spacing
      const maxWidths = {};
      const range = window.XLSX.utils.decode_range(ws['!ref'] || 'A1');
      
      // Calculate max width for each column and format UHID/serialNumber as text
      for (let C = range.s.c; C <= range.e.c; ++C) {
        let maxWidth = 12; // Increased minimum width for better spacing
        const col = window.XLSX.utils.encode_col(C);
        
        // Check header
        const headerCell = ws[col + '1'];
        const headerValue = headerCell?.v?.toString().toLowerCase() || '';
        const isUHIDColumn = headerValue.includes('uhid') || headerValue.includes('serial') || headerValue.includes('sno');
        
        if (headerCell && headerCell.v) {
          maxWidth = Math.max(maxWidth, String(headerCell.v).length);
        }
        
        // Check data cells and format UHID/serialNumber as text
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
          const cellAddress = window.XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && cell.v !== undefined && cell.v !== null) {
            const cellValue = String(cell.v);
            maxWidth = Math.max(maxWidth, cellValue.length);
            
            // Format patientUHID and serialNumber as text to prevent date formatting
            if (isUHIDColumn) {
              // Force text format by prepending with apostrophe (Excel treats this as text)
              const textValue = String(cell.v);
              // Set cell type to string and format as text
              ws[cellAddress].t = 's'; // String type
              ws[cellAddress].z = '@'; // Text format
              // Prepend with apostrophe to force Excel to treat as text (prevents date auto-format)
              ws[cellAddress].v = textValue.startsWith("'") ? textValue : `'${textValue}`;
            }
          }
        }
        
        // Set width with padding (add 3 for better spacing)
        maxWidths[col] = { wch: Math.min(maxWidth + 3, 50) }; // Cap at 50 characters
      }
      
      ws['!cols'] = Object.values(maxWidths);
      
      window.XLSX.utils.book_append_sheet(wb, ws, sheetName);
      window.XLSX.writeFile(wb, `${filename}.xlsx`);
      return true;
    } else {
      // Fallback to CSV if xlsx not available
      exportToCSV(data, filename);
      return true;
    }
  } catch (error) {
    console.error('Export error:', error);
    // Always fallback to CSV if Excel export fails
    try {
      exportToCSV(data, filename);
      return true;
    } catch (csvError) {
      console.error('CSV export also failed:', csvError);
      return false;
    }
  }
};

// CSV export fallback - improved version
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  try {
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Handle different data types and escape properly
          let processedValue = value;
          
          if (value === null || value === undefined) {
            processedValue = '';
          } else if (typeof value === 'object') {
            processedValue = JSON.stringify(value);
          } else {
            processedValue = String(value);
          }
          
          // Escape commas and quotes in CSV
          if (processedValue.includes(',') || processedValue.includes('"') || processedValue.includes('\n')) {
            processedValue = `"${processedValue.replace(/"/g, '""')}"`;
          }
          
          return processedValue;
        }).join(',')
      )
    ].join('\n');

    // Add BOM for proper Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (!isDownloadSupported()) {
      throw new Error('Download not supported in this browser. Please use a modern browser.');
    }
    
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('CSV export error:', error);
    throw new Error(`CSV export failed: ${error.message}`);
  }
};

// PDF export utility using jsPDF
const exportToPDF = (data, filename, reportTitle, columns, dateRange = null) => {
  try {
    if (!jsPDF) {
      throw new Error('jsPDF library not available. Please ensure jspdf is installed.');
    }

    const doc = new jsPDF('landscape', 'mm', 'a4'); // Landscape orientation for better table display
    
    // Page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const startY = 20;
    let currentY = startY;

    // Helper function to format dates in DD-MMM-YYYY format
    const formatDate = (dateStr) => {
      try {
        const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch (e) {
        return dateStr;
      }
    };

    // Header
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(reportTitle, pageWidth / 2, currentY, { align: 'center' });
    currentY += 8;

    // Date range (from-to) if provided - revert to original format
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const formatDateForRange = (dateStr) => {
        try {
          const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        } catch (e) {
          return dateStr;
        }
      };
      const fromDate = formatDateForRange(dateRange.startDate);
      const toDate = formatDateForRange(dateRange.endDate);
      doc.text(`Date Range: ${fromDate} to ${toDate}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 6;
    }

    // Report date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const reportDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated on: ${reportDate}`, pageWidth / 2, currentY, { align: 'center' });
    currentY += 10;

    // Table setup
    const rowHeight = 12; // Increased row height so all entries are fully visible
    const headerHeight = 12; // Increased header height for better visibility
    const fontSize = 9; // Increased font size for better visibility

    // Process columns - support both object format {header, dataKey} and string format
    let tableColumns = [];
    if (columns && Array.isArray(columns) && columns.length > 0) {
      // Check if columns are objects with header and dataKey
      if (typeof columns[0] === 'object' && columns[0].header && columns[0].dataKey) {
        tableColumns = columns.map(col => ({
          header: col.header,
          dataKey: col.dataKey
        }));
      } else {
        // Legacy format: array of strings - convert to object format
        tableColumns = columns.map(col => ({
          header: col.charAt(0).toUpperCase() + col.slice(1).replace(/([A-Z])/g, ' $1'),
          dataKey: col
        }));
      }
    } else if (data.length > 0) {
      // Auto-detect columns if not provided
      const keys = Object.keys(data[0]).filter(key => 
        !['_id', '__v', 'createdAt', 'updatedAt'].includes(key)
      );
      tableColumns = keys.map(key => ({
        header: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        dataKey: key
      }));
    }

    if (!tableColumns || tableColumns.length === 0) {
      throw new Error('No columns available for PDF export');
    }

    // Calculate column widths - no spacing between header cells (they should touch)
    const availableWidth = pageWidth - (2 * margin);
    const numColumns = tableColumns.length;
    const minColWidth = 25; // Increased minimum for better readability
    const maxColWidth = 45; // Increased maximum for better spacing
    const columnSpacing = 0; // No spacing between header cells - they should be continuous
    let colWidth = Math.min(maxColWidth, Math.max(minColWidth, availableWidth / numColumns));
    
    // Adjust if total width exceeds page
    const totalWidth = colWidth * numColumns;
    if (totalWidth > availableWidth) {
      colWidth = availableWidth / numColumns;
    }

    // Draw table header
    doc.setFontSize(fontSize);
    doc.setFont(undefined, 'bold');
    
    let xPos = margin;
    tableColumns.forEach((col, colIndex) => {
      // Draw header cell background (blue) - no spacing, cells touch each other
      doc.setFillColor(66, 139, 202);
      const cellX = xPos; // No spacing, cells are continuous
      doc.rect(cellX, currentY, colWidth, headerHeight, 'F');
      
      // Set text color to white for header text (must be set after each rect fill)
      doc.setTextColor(255, 255, 255);
      
      // Calculate text width to properly center it
      const textWidth = doc.getTextWidth(col.header);
      const textX = cellX + (colWidth / 2) - (textWidth / 2);
      const textY = currentY + (headerHeight / 2) + 2;
      
      // If text is too wide, use splitTextToSize and center the first line
      if (textWidth > colWidth - 4) {
        const splitText = doc.splitTextToSize(col.header, colWidth - 4);
        const firstLineY = currentY + (headerHeight / 2) - ((splitText.length - 1) * 3);
        splitText.forEach((line, lineIndex) => {
          const lineTextWidth = doc.getTextWidth(line);
          const lineTextX = cellX + (colWidth / 2) - (lineTextWidth / 2);
          // Ensure text color is white for each line
          doc.setTextColor(255, 255, 255);
          doc.text(line, lineTextX, firstLineY + (lineIndex * 6));
        });
      } else {
        // Draw header text centered
        doc.text(col.header, textX, textY);
      }
      
      xPos += colWidth; // No spacing between header cells
    });
    
    currentY += headerHeight;
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');

    // Draw table rows
    data.forEach((row, rowIndex) => {
      // Check if we need a new page
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage();
        currentY = startY;
        
        // Redraw header on new page
        doc.setFontSize(fontSize);
        doc.setFont(undefined, 'bold');
        xPos = margin;
        tableColumns.forEach((col, colIndex) => {
          // Draw header cell background (blue) - no spacing, cells touch each other
          doc.setFillColor(66, 139, 202);
          const cellX = xPos; // No spacing, cells are continuous
          doc.rect(cellX, currentY, colWidth, headerHeight, 'F');
          
          // Set text color to white for header text (must be set after each rect fill)
          doc.setTextColor(255, 255, 255);
          
          // Calculate text width to properly center it
          const textWidth = doc.getTextWidth(col.header);
          const textX = cellX + (colWidth / 2) - (textWidth / 2);
          const textY = currentY + (headerHeight / 2) + 2;
          
          // If text is too wide, use splitTextToSize and center the first line
          if (textWidth > colWidth - 4) {
            const splitText = doc.splitTextToSize(col.header, colWidth - 4);
            const firstLineY = currentY + (headerHeight / 2) - ((splitText.length - 1) * 3);
            splitText.forEach((line, lineIndex) => {
              const lineTextWidth = doc.getTextWidth(line);
              const lineTextX = cellX + (colWidth / 2) - (lineTextWidth / 2);
              // Ensure text color is white for each line
              doc.setTextColor(255, 255, 255);
              doc.text(line, lineTextX, firstLineY + (lineIndex * 6));
            });
          } else {
            // Draw header text centered
            doc.text(col.header, textX, textY);
          }
          
          xPos += colWidth; // No spacing between header cells
        });
        currentY += headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
      }

      // Alternate row colors
      if (rowIndex % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        const totalTableWidth = colWidth * numColumns; // No spacing
        doc.rect(margin, currentY, totalTableWidth, rowHeight, 'F');
      }

      // Draw row data - align with headers (matching column positions)
      xPos = margin;
      tableColumns.forEach((col, colIndex) => {
        let cellValue = row[col.dataKey];
        
        // Format cell value
        if (cellValue === null || cellValue === undefined) {
          cellValue = '';
        } else if (typeof cellValue === 'object') {
          cellValue = JSON.stringify(cellValue);
        } else {
          cellValue = String(cellValue);
        }

        // Match header cell position (no spacing)
        const cellX = xPos;
        
        // Calculate text width for proper alignment (center like headers)
        const textWidth = doc.getTextWidth(cellValue);
        let textX = cellX + 2; // Default left align with padding
        
        // Center align if text fits within column
        if (textWidth <= colWidth - 4) {
          textX = cellX + (colWidth / 2) - (textWidth / 2);
        }
        
        // Use splitTextToSize for long text to prevent overflow
        const cellTextY = currentY + 6; // Increased vertical padding for better visibility
        if (textWidth > colWidth - 4) {
          const splitText = doc.splitTextToSize(cellValue, colWidth - 4);
          const lineHeight = 5;
          splitText.forEach((line, lineIndex) => {
            const lineTextWidth = doc.getTextWidth(line);
            const lineTextX = cellX + (colWidth / 2) - (lineTextWidth / 2);
            doc.text(line, lineTextX, cellTextY + (lineIndex * lineHeight));
          });
        } else {
          doc.text(cellValue, textX, cellTextY);
        }
        
        xPos += colWidth; // No spacing between cells
      });

      currentY += rowHeight;
    });

    // Footer with page numbers
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    doc.save(`${filename}.pdf`);
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

// Generic export function that gets data and exports it
const exportReportData = async (apiFunction, params, filename, sheetName, columnOrder = null) => {
  try {
    // Validate input parameters
    if (!apiFunction || typeof apiFunction !== 'function') {
      throw new Error('Invalid API function provided');
    }
    
    if (!filename || typeof filename !== 'string') {
      throw new Error('Invalid filename provided');
    }

    // Check if browser supports downloads
    if (!isDownloadSupported()) {
      throw new Error('Download not supported in this browser. Please use a modern browser.');
    }

    // Call the API to get data
    const response = await apiFunction(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    
    // Extract data based on different response formats
    if (response?.data?.payments) {
      data = response.data.payments;
    } else if (response?.data?.items) {
      data = response.data.items;
    } else if (response?.data?.records) {
      data = response.data.records;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else if (Array.isArray(response)) {
      data = response;
    } else if (response?.data && typeof response.data === 'object') {
      // Handle case where response.data is a single object
      data = [response.data];
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data)) {
      throw new Error('Data is not in array format');
    }

    if (data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export (remove internal fields, format dates, etc.)
    let cleanedData = data.map(item => {
      const cleaned = { ...item };
      
      // Remove internal MongoDB fields
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Map data keys to match column dataKeys (for IPD expense detail statement and expense report)
      // Ensure we have the right field names for export
      if (cleaned.uhid === undefined && cleaned.patientUHID !== undefined) {
        cleaned.uhid = cleaned.patientUHID;
      } else if (cleaned.uhid === undefined && cleaned.UHID !== undefined) {
        cleaned.uhid = cleaned.UHID;
      }
      
      if (cleaned.patientName === undefined) {
        cleaned.patientName = `${cleaned.firstName || ''} ${cleaned.lastName || ''}`.trim() || cleaned.patient?.name || '-';
      }
      
      // Handle regDate (for IPD expense detail statement) - match table transformation
      if (cleaned.regDate === undefined) {
        const regDateValue = cleaned.regDate || cleaned.admissionDate || cleaned.visitDate || cleaned.consultationDateTime || cleaned.createdAt;
        if (regDateValue) {
          try {
            const date = new Date(regDateValue);
            if (!isNaN(date.getTime())) {
              cleaned.regDate = date.toLocaleDateString('en-GB'); // Match table format
            } else {
              cleaned.regDate = '';
            }
          } catch (e) {
            cleaned.regDate = '';
          }
        } else {
          cleaned.regDate = '';
        }
      } else if (cleaned.regDate) {
        // Format if it's a date string
        try {
          const date = new Date(cleaned.regDate);
          if (!isNaN(date.getTime())) {
            cleaned.regDate = date.toLocaleDateString('en-GB'); // Match table format
          }
        } catch (e) {
          // Keep original value
        }
      }
      
      // Handle dischargeDate (for IPD expense detail statement) - match table transformation
      if (cleaned.dischargeDate === undefined || cleaned.dischargeDate === null || cleaned.dischargeDate === '') {
        cleaned.dischargeDate = '-'; // Match table default
      } else {
        // Format if it's a date string
        try {
          const date = new Date(cleaned.dischargeDate);
          if (!isNaN(date.getTime())) {
            cleaned.dischargeDate = date.toLocaleDateString('en-GB'); // Match table format
          }
        } catch (e) {
          // Keep original value or default to '-'
          if (!cleaned.dischargeDate) {
            cleaned.dischargeDate = '-';
          }
        }
      }
      
      // Handle consultation charges (for IPD expense detail statement) - match table transformation exactly
      if (cleaned.consulatationCharges === undefined) {
        cleaned.consulatationCharges = cleaned.consultationCharges || cleaned.amount || '0';
      }
      // Keep as string/number - no formatting to match table
      
      // Handle procedure charges (for IPD expense detail statement) - match table transformation exactly
      if (cleaned.procedureCharges === undefined || cleaned.procedureCharges === null || cleaned.procedureCharges === '') {
        cleaned.procedureCharges = '0';
      }
      // Keep as string/number - no formatting to match table
      
      // Format dates and handle null values
      // IMPORTANT: Check for UHID/serialNumber FIRST before date parsing
      // Also check for regDate and dischargeDate which are already formatted in table
      Object.keys(cleaned).forEach(key => {
        const value = cleaned[key];
        
        if (value === null || value === undefined) {
          cleaned[key] = '';
        } else if (key === 'patientUHID' || key === 'serialNumber' || key === 'uhid') {
          // Format UHID and serial number as text FIRST - prevent date parsing
          // Prepend with apostrophe to force Excel to treat as text
          cleaned[key] = String(value);
        } else if (key === 'regDate' || key === 'dischargeDate') {
          // regDate and dischargeDate are already formatted in table as 'en-GB' locale
          // Keep them as-is to match table display exactly
          cleaned[key] = String(value);
        } else if (value instanceof Date) {
          // Format dates to match table format (en-GB locale: DD/MM/YYYY)
          const day = String(value.getDate()).padStart(2, '0');
          const month = String(value.getMonth() + 1).padStart(2, '0');
          const year = value.getFullYear();
          cleaned[key] = `${day}/${month}/${year}`;
        } else if (typeof value === 'string' && !isNaN(Date.parse(value))) {
          // Only parse as date if it's NOT a UHID-like value and NOT already formatted
          // UHID values typically contain letters or dashes (e.g., "KSG-00181352")
          const isLikelyUHID = /^[A-Za-z]+-?\d+$/.test(value) || /^\d{4,}$/.test(value);
          // Check if it's already in DD/MM/YYYY format (table format)
          const isAlreadyFormatted = /^\d{2}\/\d{2}\/\d{4}$/.test(value);
          if (!isLikelyUHID && !isAlreadyFormatted) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                // Format to match table format (en-GB locale: DD/MM/YYYY)
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                cleaned[key] = `${day}/${month}/${year}`;
              }
            } catch (dateError) {
              // Keep original value if date parsing fails
              cleaned[key] = value;
            }
          } else {
            // Likely UHID or already formatted - keep as string
            cleaned[key] = value;
          }
        } else if (typeof value === 'object' && value !== null) {
          // Convert objects to JSON strings for CSV export
          try {
            cleaned[key] = JSON.stringify(value);
          } catch (jsonError) {
            cleaned[key] = String(value);
          }
        }
      });
      
      return cleaned;
    });

    // Reorder columns if columnOrder is provided
    if (columnOrder && Array.isArray(columnOrder) && columnOrder.length > 0) {
      // Extract column keys from columnOrder (support both string and object formats)
      const orderedKeys = columnOrder.map(col => {
        if (typeof col === 'string') {
          return col;
        } else if (typeof col === 'object' && col.dataKey) {
          return col.dataKey;
        } else if (typeof col === 'object' && col.value) {
          return col.value;
        }
        return col;
      });

      // Reorder data to match column order
      cleanedData = cleanedData.map(item => {
        const reordered = {};
        // First, add columns in the specified order
        orderedKeys.forEach(key => {
          if (item.hasOwnProperty(key)) {
            reordered[key] = item[key];
          }
        });
        // Then, add any remaining columns that weren't in the order list
        Object.keys(item).forEach(key => {
          if (!orderedKeys.includes(key)) {
            reordered[key] = item[key];
          }
        });
        return reordered;
      });
    }

    // Attempt to export
    const success = exportToExcel(cleanedData, filename, sheetName);
    if (!success) {
      throw new Error('Export operation failed');
    }
    
    return { success: true, count: data.length };
  } catch (error) {
    console.error('Export error:', error);
    
    // Provide more specific error messages
    if (error.message.includes('No data available')) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    } else if (error.message.includes('API')) {
      throw new Error('Failed to fetch data from server. Please check your connection and try again.');
    } else if (error.message.includes('Export operation failed')) {
      throw new Error('Export operation failed. Please try again or contact support.');
    } else if (error.message.includes('Download not supported')) {
      throw new Error('Your browser does not support file downloads. Please use a modern browser like Chrome, Firefox, or Edge.');
    } else {
      throw new Error(`Export failed: ${error.message}`);
    }
  }
};

const REPORTS_PATH = "/apis/reports/v1";

// Payment Collection Report
export const getPaymentCollectionApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/payment-collection`, params, options);

// Cash Collection Report (Alias for payment collection)
export const getCashCollectionApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/payment-collection`, params, options);

// Summary Bill Report
export const getSummaryBillApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/summary-bill`, params, options);

// Cancel Payment Report
export const getCancelPaymentApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/cancel-payment`, params, options);

// Collection Report
export const getCollectionReportApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/collection`, params, options);

// Bill Detail Report
export const getBillDetailApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/bill-detail`, params, options);

// Advance Payment Report
export const getAdvancePaymentApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/advance-payment`, params, options);

// Package Count Statement Report
export const getPackageCountApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/package-count-statement`, params, options);

// IPD Discharge Patient Report
export const getIPDDischargePatientApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/ipd-discharge-patient`, params, options);

// IPD Patient Summary Report
export const getIPDPatientSummaryApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/ipd-patient-summary`, params, options);

// Patient Indent Ledger Report
export const getPatientIndentLedgerApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/patient-indent`, params, options);

// Patient Indent Return Ledger Report
export const getPatientIndentReturnApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/patient-indent-return`, params, options);

// Patient Ward Issue Report
export const getPatientWardIssueApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/patient-ward-issue`, params, options);

// Bed Occupancy Report
export const getBedOccupancyApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/bed-occupancy`, params, options);

// Finance Dashboard Report
export const getFinanceDashboardApi = async (params, options = {}) => await getRequest(`${REPORTS_PATH}/finance-dashboard`, params, options);

// Updated export functions that handle JSON to Excel conversion
export const exportPaymentCollectionApi = async (params) =>
  exportReportData(getPaymentCollectionApi, params, 'payment-collection-report', 'Payment Collection');

export const exportCashCollectionApi = async (params, columnOrder = null) =>
  exportReportData(getCashCollectionApi, params, 'cash-collection-report', 'Cash Collection', columnOrder);

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);

/** Cash Collection Table tab: export summary rows only (uses summaryOnly API). */
export const exportCashCollectionSummaryExcel = async (params) => {
  const res = await getCashCollectionApi({ ...params, summaryOnly: 'true' });
  const s = res?.data?.summary;
  if (!s) throw new Error('No summary in API response');
  const net =
    Number(s.cleared?.totalAmount || 0) -
    Number(s.cancelled?.totalAmount || 0) -
    Number(s.refund?.totalAmount || 0);
  const rows = [
    { Line: 'Total Cleared', Count: s.cleared?.count ?? 0, Amount: formatInr(s.cleared?.totalAmount) },
    { Line: 'Total Cancelled', Count: s.cancelled?.count ?? 0, Amount: formatInr(s.cancelled?.totalAmount) },
    { Line: 'Total Refund', Count: s.refund?.count ?? 0, Amount: formatInr(s.refund?.totalAmount) },
    { Line: 'Net Total', Count: '', Amount: formatInr(net) },
  ];
  const ok = exportToExcel(rows, 'cash-collection-report', 'Cash Collection Summary');
  if (!ok) throw new Error('Export operation failed');
  return { success: true, count: rows.length };
};

export const exportCashCollectionSummaryPDF = async (params, dateRange = null) => {
  const res = await getCashCollectionApi({ ...params, summaryOnly: 'true' });
  const s = res?.data?.summary;
  if (!s) throw new Error('No summary in API response');
  const net =
    Number(s.cleared?.totalAmount || 0) -
    Number(s.cancelled?.totalAmount || 0) -
    Number(s.refund?.totalAmount || 0);
  const data = [
    { desc: 'Cleared', count: String(s.cleared?.count ?? 0), amount: formatInr(s.cleared?.totalAmount) },
    { desc: 'Cancelled', count: String(s.cancelled?.count ?? 0), amount: formatInr(s.cancelled?.totalAmount) },
    { desc: 'Refund', count: String(s.refund?.count ?? 0), amount: formatInr(s.refund?.totalAmount) },
    { desc: 'Net Total', count: '', amount: formatInr(net) },
  ];
  const columns = [
    { header: 'Category', dataKey: 'desc' },
    { header: 'Count', dataKey: 'count' },
    { header: 'Amount (INR)', dataKey: 'amount' },
  ];
  exportToPDF(data, 'cash-collection-report', 'Cash Collection Summary', columns, dateRange);
  return { success: true, count: data.length };
};

export const exportSummaryBillApi = async (params, columnOrder = null) =>
  exportReportData(getSummaryBillApi, params, 'summary-bill-report', 'Summary Bill', columnOrder);

// PDF export functions
export const exportCashCollectionPDF = async (params, columns, dateRange = null) => {
  try {
    const response = await getCashCollectionApi(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    if (response?.data?.payments) {
      data = response.data.payments;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export
    const cleanedData = data.map((item, index) => {
      const cleaned = { ...item };
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Add serial number if not present
      if (!cleaned.serialNumber) {
        cleaned.serialNumber = index + 1;
      }
      
      // Format dates
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
          cleaned[key] = JSON.stringify(cleaned[key]);
        }
      });
      
      return cleaned;
    });

    exportToPDF(cleanedData, 'cash-collection-report', 'Cash Collection Report', columns, dateRange);
    return { success: true, count: data.length };
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

export const exportSummaryBillPDF = async (params, columns, dateRange = null) => {
  try {
    const response = await getSummaryBillApi(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    if (response?.data?.items) {
      data = response.data.items;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export
    const cleanedData = data.map(item => {
      const cleaned = { ...item };
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Format dates and numbers
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
          cleaned[key] = JSON.stringify(cleaned[key]);
        }
      });
      
      return cleaned;
    });

    exportToPDF(cleanedData, 'summary-bill-report', 'Summary Bill Report', columns, dateRange);
    return { success: true, count: data.length };
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

export const exportCancelPaymentApi = async (params) =>
  exportReportData(getCancelPaymentApi, params, 'cancel-payment-report', 'Cancel Payment');

export const exportCollectionReportApi = async (params) =>
  exportReportData(getCollectionReportApi, params, 'collection-report', 'Collection');

export const exportBillDetailApi = async (params) =>
  exportReportData(getBillDetailApi, params, 'bill-detail-report', 'Bill Detail');

export const exportAdvancePaymentApi = async (params, columnOrder = null) =>
  exportReportData(getAdvancePaymentApi, params, 'advance-payment-report', 'Advance Payment', columnOrder);

export const exportAdvancePaymentPDF = async (params, columns, dateRange = null) => {
  try {
    const response = await getAdvancePaymentApi(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    if (response?.data?.records && Array.isArray(response.data.records)) {
      data = response.data.records;
    } else if (response?.data?.payments && Array.isArray(response.data.payments)) {
      data = response.data.payments;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export
    const cleanedData = data.map((item, index) => {
      const cleaned = { ...item };
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Format dates
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
          cleaned[key] = JSON.stringify(cleaned[key]);
        }
      });
      
      return cleaned;
    });

    exportToPDF(cleanedData, 'advance-payment-report', 'Patient Advance Payment Report', columns, dateRange);
    return { success: true, count: data.length };
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

export const exportPackageCountApi = async (params, columnOrder = null) =>
  exportReportData(getPackageCountApi, params, 'package-count-report', 'Package Count', columnOrder);

export const exportPackageCountPDF = async (params, columns, dateRange = null) => {
  try {
    const response = await getPackageCountApi(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    if (response?.data?.records && Array.isArray(response.data.records)) {
      data = response.data.records;
    } else if (response?.data?.packages && Array.isArray(response.data.packages)) {
      data = response.data.packages;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export
    const cleanedData = data.map((item, index) => {
      const cleaned = { ...item };
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Format dates
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
          cleaned[key] = JSON.stringify(cleaned[key]);
        }
      });
      
      return cleaned;
    });

    exportToPDF(cleanedData, 'package-count-report', 'Month Wise Package Count Statement (IPD)', columns, dateRange);
    return { success: true, count: data.length };
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

export const exportIPDDischargePatientApi = async (params) =>
  exportReportData(getIPDDischargePatientApi, params, 'ipd-discharge-patient-report', 'IPD Discharge');

export const exportIPDPatientSummaryApi = async (params) =>
  exportReportData(getIPDPatientSummaryApi, params, 'ipd-patient-summary-report', 'IPD Summary');

export const exportPatientIndentLedgerApi = async (params) =>
  exportReportData(getPatientIndentLedgerApi, params, 'patient-indent-ledger-report', 'Patient Indent');

export const exportPatientIndentReturnApi = async (params) =>
  exportReportData(getPatientIndentReturnApi, params, 'patient-indent-return-report', 'Indent Return');

export const exportPatientWardIssueApi = async (params) =>
  exportReportData(getPatientWardIssueApi, params, 'patient-ward-issue-report', 'Ward Issue');

export const exportBedOccupancyApi = async (params) =>
  exportReportData(getBedOccupancyApi, params, 'bed-occupancy-report', 'Bed Occupancy');

export const exportFinanceDashboardApi = async (params) =>
  exportReportData(getFinanceDashboardApi, params, 'finance-dashboard-report', 'Finance Dashboard');

// Expense Report Export
export const exportExpenseReportApi = async (params, columnOrder = null) => {
  // Import the expense report API function dynamically to avoid circular dependency
  const { exportExpenseReportApi: getExpenseReportData } = await import('./expenseReport.service');
  return await exportReportData(getExpenseReportData, params, 'expense-report', 'Expense Report', columnOrder);
};

export const exportExpenseReportPDF = async (params, columns, dateRange = null, reportTitle = 'Patient Wise Item Wise Expense Report') => {
  try {
    // Import the expense report API function dynamically to avoid circular dependency
    const { getExpenseReportApi } = await import('./expenseReport.service');
    const response = await getExpenseReportApi(params);
    
    if (!response) {
      throw new Error('No response received from API');
    }

    let data = [];
    if (response?.data?.data && Array.isArray(response.data.data)) {
      data = response.data.data;
    } else if (response?.data && Array.isArray(response.data)) {
      data = response.data;
    } else {
      throw new Error('No exportable data found in API response');
    }

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No data available to export. Please check your search criteria and try again.');
    }

    // Clean data for export
    const cleanedData = data.map((item, index) => {
      const cleaned = { ...item };
      delete cleaned._id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      
      // Map data keys to match column dataKeys
      // Ensure we have the right field names for PDF export
      if (cleaned.uhid === undefined && cleaned.patientUHID !== undefined) {
        cleaned.uhid = cleaned.patientUHID;
      } else if (cleaned.uhid === undefined && cleaned.UHID !== undefined) {
        cleaned.uhid = cleaned.UHID;
      }
      
      if (cleaned.patientName === undefined) {
        cleaned.patientName = `${cleaned.firstName || ''} ${cleaned.lastName || ''}`.trim() || cleaned.patient?.name || '-';
      }
      
      // Handle visitDate (for expense report) or regDate (for IPD expense detail statement) - match table transformation
      if (cleaned.visitDate === undefined && cleaned.regDate === undefined) {
        const regDateValue = cleaned.regDate || cleaned.admissionDate || cleaned.visitDate || cleaned.consultationDateTime || cleaned.createdAt;
        if (regDateValue) {
          try {
            const date = new Date(regDateValue);
            if (!isNaN(date.getTime())) {
              cleaned.regDate = date.toLocaleDateString('en-GB'); // Match table format
              cleaned.visitDate = cleaned.regDate;
            } else {
              cleaned.regDate = '';
              cleaned.visitDate = '';
            }
          } catch (e) {
            cleaned.regDate = '';
            cleaned.visitDate = '';
          }
        } else {
          cleaned.regDate = '';
          cleaned.visitDate = '';
        }
      } else if (cleaned.regDate === undefined && cleaned.visitDate !== undefined) {
        // Format regDate from visitDate
        try {
          const date = new Date(cleaned.visitDate);
          if (!isNaN(date.getTime())) {
            cleaned.regDate = date.toLocaleDateString('en-GB'); // Match table format
          } else {
            cleaned.regDate = '';
          }
        } catch (e) {
          cleaned.regDate = '';
        }
      } else if (cleaned.regDate) {
        // Format regDate if it exists
        try {
          const date = new Date(cleaned.regDate);
          if (!isNaN(date.getTime())) {
            cleaned.regDate = date.toLocaleDateString('en-GB'); // Match table format
          }
        } catch (e) {
          // Keep original value
        }
      }
      
      // Handle dischargeDate (for IPD expense detail statement) - match table transformation
      if (cleaned.dischargeDate === undefined || cleaned.dischargeDate === null || cleaned.dischargeDate === '') {
        cleaned.dischargeDate = '-'; // Match table default
      } else {
        // Format if it's a date string
        try {
          const date = new Date(cleaned.dischargeDate);
          if (!isNaN(date.getTime())) {
            cleaned.dischargeDate = date.toLocaleDateString('en-GB'); // Match table format
          }
        } catch (e) {
          // Keep original value or default to '-'
          if (!cleaned.dischargeDate) {
            cleaned.dischargeDate = '-';
          }
        }
      }
      
      // Handle consultation charges (for IPD expense detail statement) - match table transformation exactly
      if (cleaned.consulatationCharges === undefined) {
        cleaned.consulatationCharges = cleaned.consultationCharges || cleaned.amount || '0';
      }
      // Keep as string/number - no formatting to match table
      
      // Handle procedure charges (for IPD expense detail statement) - match table transformation exactly
      if (cleaned.procedureCharges === undefined || cleaned.procedureCharges === null || cleaned.procedureCharges === '') {
        cleaned.procedureCharges = '0';
      }
      // Keep as string/number - no formatting to match table
      
      // Handle itemService (for expense report)
      if (cleaned.itemService === undefined) {
        cleaned.itemService = cleaned.itemName || cleaned.serviceName || '-';
      }
      
      // Format dates to readable format
      if (cleaned.visitDate) {
        try {
          const date = new Date(cleaned.visitDate);
          if (!isNaN(date.getTime())) {
            cleaned.visitDate = date.toLocaleDateString('en-GB');
          }
        } catch (e) {
          // Keep original value if date parsing fails
        }
      }
      
      // Format regDate (for IPD expense detail statement)
      if (cleaned.regDate && cleaned.regDate !== '-') {
        try {
          const date = new Date(cleaned.regDate);
          if (!isNaN(date.getTime())) {
            cleaned.regDate = date.toLocaleDateString('en-GB');
          }
        } catch (e) {
          // Keep original value if date parsing fails
        }
      }
      
      // Format dischargeDate (for IPD expense detail statement)
      if (cleaned.dischargeDate && cleaned.dischargeDate !== '-' && cleaned.dischargeDate !== null && cleaned.dischargeDate !== '') {
        try {
          const date = new Date(cleaned.dischargeDate);
          if (!isNaN(date.getTime())) {
            cleaned.dischargeDate = date.toLocaleDateString('en-GB');
          }
        } catch (e) {
          // Keep original value if date parsing fails
        }
      }
      
      // Keep consultation and procedure charges as numbers (no currency formatting)
      // Currency formatting will be handled by PDF generation if needed
      
      // Format currency for amount
      if (cleaned.amount !== undefined && cleaned.amount !== null && cleaned.amount !== '') {
        const numAmount = Number(cleaned.amount);
        if (!isNaN(numAmount)) {
          cleaned.amount = `₹${numAmount.toLocaleString()}`;
        }
      } else if (cleaned.totalAmount !== undefined) {
        const numAmount = Number(cleaned.totalAmount);
        if (!isNaN(numAmount)) {
          cleaned.amount = `₹${numAmount.toLocaleString()}`;
        }
      } else if (cleaned.total !== undefined) {
        const numAmount = Number(cleaned.total);
        if (!isNaN(numAmount)) {
          cleaned.amount = `₹${numAmount.toLocaleString()}`;
        }
      }
      
      // Format other fields
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
          cleaned[key] = JSON.stringify(cleaned[key]);
        }
      });
      
      return cleaned;
    });

    exportToPDF(cleanedData, 'expense-report', reportTitle, columns, dateRange);
    return { success: true, count: data.length };
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
}; 